import moment from 'moment-timezone';
import {useMemo} from 'react';

import {CHAT_USE_CASE_NEW_FOR_DAYS} from 'llm/chat-library/use-cases/chatUseCaseCatalog';
import preferences from 'Preferences';
import usePreference from 'ui-library/hooks/usePreference';

import {USED_CHAT_USE_CASES_PREFERENCE_KEY} from './chatUseCaseConstants';

export {USED_CHAT_USE_CASES_PREFERENCE_KEY};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

function toUseCaseIds(value: unknown): Array<string> {
  return Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string') : [];
}

/**
 * Record that the user ran a use case. Imperative so it is callable from non-React paths such as the
 * chat-box pills' click handler.
 *
 * The stored value is re-read here rather than captured earlier, so a caller holding a stale
 * snapshot cannot drop ids this tab has already recorded. That is the only guarantee available: the
 * preference is written as a whole value with no server-side merge, and each tab has its own
 * in-memory copy seeded at page load, so a second tab that was open across this write will still
 * overwrite it. Losing an id only means a use case sorts as untried, which is worth accepting rather
 * than adding a merge endpoint for an ordering hint.
 *
 * The new ids must be a new array: `preferences.set` decides whether to persist by comparing the
 * stored value by reference, so appending in place and setting it back would never reach the server.
 */
export function markChatUseCaseUsed(chatUseCaseId: string): void {
  const usedChatUseCaseIds = toUseCaseIds(preferences.get(USED_CHAT_USE_CASES_PREFERENCE_KEY));
  if (usedChatUseCaseIds.includes(chatUseCaseId)) {
    return;
  }
  preferences.set(USED_CHAT_USE_CASES_PREFERENCE_KEY, [...usedChatUseCaseIds, chatUseCaseId]);
}

/** Reactive read of the ids the user has run, for surfaces that sort on it. */
export function useUsedChatUseCaseIds(): ReadonlySet<string> {
  const [usedChatUseCaseIds] = usePreference<unknown>(USED_CHAT_USE_CASES_PREFERENCE_KEY);
  return useMemo(() => new Set(toUseCaseIds(usedChatUseCaseIds)), [usedChatUseCaseIds]);
}

/** The subset of a use case that determines where it sorts and whether it reads as new. */
export interface ChatUseCaseDiscoveryFields {
  id: string;
  addedAt?: string;
}

/**
 * Whether to present the use case as new: added recently enough to still be worth pointing out, and
 * not yet run by this user, since a use case they have already run is not news to them.
 */
export function isChatUseCaseNew(
  chatUseCase: ChatUseCaseDiscoveryFields,
  usedIds: ReadonlySet<string>,
  now: number = Date.now()
): boolean {
  if (chatUseCase.addedAt == null || usedIds.has(chatUseCase.id)) {
    return false;
  }
  const addedAt = moment.utc(chatUseCase.addedAt, moment.ISO_8601);
  if (!addedAt.isValid()) {
    return false;
  }
  // A date in the future is an announcement that hasn't started yet, so it is not new either.
  const ageMs = now - addedAt.valueOf();
  return ageMs >= 0 && ageMs <= CHAT_USE_CASE_NEW_FOR_DAYS * MS_PER_DAY;
}

/**
 * Lifts new use cases to the front and leaves everything else in catalog order.
 *
 * For surfaces that badge a use case the user has already run: the badge says everything the sort
 * would, and moving a row the user has just used pulls it out from under them, so having run
 * something is deliberately not allowed to change where it sits.
 */
export function sortNewFirst<T extends ChatUseCaseDiscoveryFields>(
  items: Array<T>,
  usedIds: ReadonlySet<string>,
  now: number = Date.now()
): Array<T> {
  const isNew = (item: T): boolean => isChatUseCaseNew(item, usedIds, now);
  return [...items.filter((item) => isNew(item)), ...items.filter((item) => !isNew(item))];
}

/**
 * Discovery order: new use cases first, then the rest of the untried ones, then the ones already
 * run. Stable within each band, so catalog order survives.
 *
 * For surfaces with no room to say which use cases have been run, where sinking them is the only
 * way to lead with something the user hasn't seen. Where there is room to badge them, prefer
 * {@link sortNewFirst}.
 */
export function sortForDiscovery<T extends ChatUseCaseDiscoveryFields>(
  items: Array<T>,
  usedIds: ReadonlySet<string>,
  now: number = Date.now()
): Array<T> {
  const isNew = (item: T): boolean => isChatUseCaseNew(item, usedIds, now);
  return [
    ...items.filter((item) => isNew(item)),
    ...items.filter((item) => !isNew(item) && !usedIds.has(item.id)),
    ...items.filter((item) => usedIds.has(item.id)),
  ];
}
